import {
  AccosListPageFilterLocalStorage,
  EnquiryDataLocalStorage,
  LandingPagePackageIdLocalStorage,
  LandingPageRoomIdLocalStorage,
  MaximalAmountGeneralEnquiryTickets,
  mediaBreakpoints,
  RequestedCidsLocalStorage
} from '@/constants'
import t from '@/i18n/translations'
import { enquiryBarOrigin, enquiryData, enquirySubmittedSuccessfully, landingPagePackageId, landingPageRoomId } from '@/store/enquiry.store'
import { filtersData, filterStore } from '@/store/filter.store'
import dayjs from 'dayjs'
import { ref } from 'vue'
import { broadcastChannelMessages, CULTURE, PID } from './helpers'
import type { IListAccosApiResponse } from './interfaces/apiInterfaces'
import {
  EnquiryBarOrigin,
  EnquiryType,
  type IAccosFilter,
  type IEnquiryDto,
  type IEnquiryFilter,
  type IEnquiryFormValues,
  type IFilterStoreInterface
} from './interfaces/appInterfaces'
import { useGeneralEnquiryAccosApi } from './useApi'

export const titles = (): Array<{ name: string; value: number }> => [
  { name: '', value: 0 },
  { name: t(`${CULTURE}.enquiry.form.anagraph.titles.mr`), value: 1 },
  { name: t(`${CULTURE}.enquiry.form.anagraph.titles.mrs`), value: 2 },
  { name: t(`${CULTURE}.enquiry.form.anagraph.titles.family`), value: 3 },
  { name: t(`${CULTURE}.enquiry.form.anagraph.titles.org`), value: 4 }
]

export const boardingsGeneralText = (): string => {
  if (filterStore.value.boardingIds != null && filtersData.value != null) {
    const boardingInFilterStore = filtersData.value.boardings.find((x) => x.id === filterStore.value.boardingIds[0])
    return boardingInFilterStore?.name ?? t(`${CULTURE}.filter.boardingAny`)
  }
  return t(`${CULTURE}.filter.boardingAny`)
}

export async function getAccosForGeneralEnquiry(): Promise<IListAccosApiResponse | undefined> {
  if (enquiryBarOrigin.value === EnquiryBarOrigin.AccosUserList) {
    const result = await useGeneralEnquiryAccosApi(castFilterStoreToAccosFilter(filterStore.value))
    return result.data ?? undefined
  } else {
    const storedFilter = localStorage.getItem(AccosListPageFilterLocalStorage)
    if (storedFilter != null) {
      const parsedFilter = JSON.parse(storedFilter) as IFilterStoreInterface
      const result = await useGeneralEnquiryAccosApi(castFilterStoreToAccosFilter(parsedFilter))
      return result.data ?? undefined
    }
  }
}

export const getPhonePlaceholder = (): string => {
  let placeholderPhone: string

  switch (CULTURE) {
    case 'de':
      placeholderPhone = '(0049 1512 3456789)'
      break
    case 'it':
      placeholderPhone = '(0039 320 123 4567)'
      break
    case 'en':
      placeholderPhone = ''
      break
    case 'fr':
      placeholderPhone = '(0033 6 12 34 56 78)'
      break
    case 'pl':
      placeholderPhone = '(0048 501 234 567)'
      break
    case 'nl':
      placeholderPhone = '(0031 6 12345678)'
      break
    default:
      placeholderPhone = ''
      break
  }
  return placeholderPhone
}

export function castToEnquiryDtoAndSaveToStore(
  enquiryFormData: IEnquiryFormValues,
  directEnquiry: boolean,
  companyIds: number[],
  enquiryFilter: IEnquiryFilter,
  roomTag: string | undefined,
  packageTag: string | undefined
): IEnquiryDto {
  var data: IEnquiryDto = {
    portalId: PID,
    language: CULTURE,
    enquiryType: directEnquiry ? EnquiryType.Direct : EnquiryType.General,
    referer: undefined, // set in controller
    track: undefined, // set in controller
    ip: undefined, // set in controller

    arrival: dayjs(enquiryFormData.date[0]).format('YYYY-MM-DD'),
    departure: dayjs(enquiryFormData.date[1]).format('YYYY-MM-DD'),
    rooms: enquiryFormData.occupancy.rooms?.map((x) => ({
      adults: x.adults || 0,
      children: x.children || 0,
      childrenAges: x.childrenAges || []
    })),
    hasPets: enquiryFormData.occupancy.pets.hasPets,
    petsMessage: enquiryFormData.occupancy.pets.message,
    title: enquiryFormData.title,
    firstname: enquiryFormData.firstname,
    lastname: enquiryFormData.surname,
    email: enquiryFormData.email,
    phone: enquiryFormData.phone,
    message: enquiryFormData.message,
    privacy: enquiryFormData.privacy,
    newsletter: enquiryFormData.newsletter,

    companyIds: companyIds,
    wishedOffers: companyIds.length > MaximalAmountGeneralEnquiryTickets ? MaximalAmountGeneralEnquiryTickets : companyIds.length, //used for tickets
    filter: enquiryFilter,
    tag: roomTag || packageTag
  }

  //saving the enquiryData also in the store to be able to make a enquiry with one click on the enquirysuccesspage
  enquiryData.value = data
  localStorage.setItem(EnquiryDataLocalStorage, btoa(JSON.stringify(data)))

  return data
}

export const messageReceiverLandingPagePackageId = new BroadcastChannel(LandingPagePackageIdLocalStorage)

export const messageReceiverLandingPageRoomId = new BroadcastChannel(LandingPageRoomIdLocalStorage)

messageReceiverLandingPagePackageId.addEventListener('message', (e) => {
  switch (e.data) {
    case broadcastChannelMessages.UPDATE:
      const pid = localStorage.getItem(LandingPagePackageIdLocalStorage)
      if (pid != null) {
        landingPagePackageId.value = parseInt(pid)
        enquirySubmittedSuccessfully.value = false
      }
      break
    default:
      console.warn(`NO HANDLER FOR MESSAGE: ${e.data}`)
  }
})

messageReceiverLandingPageRoomId.addEventListener('message', (e) => {
  switch (e.data) {
    case broadcastChannelMessages.UPDATE:
      const rid = localStorage.getItem(LandingPageRoomIdLocalStorage)
      if (rid != null) {
        landingPageRoomId.value = parseInt(rid)
        enquirySubmittedSuccessfully.value = false
      }
      break
    default:
      console.warn(`NO HANDLER FOR MESSAGE: ${e.data}`)
  }
})

export const responsiveOptions = ref([
  //default numVisible = 3
  {
    breakpoint: mediaBreakpoints.lg,
    numVisible: 3,
    numScroll: 1
  },
  {
    breakpoint: mediaBreakpoints.md,
    numVisible: 2,
    numScroll: 1
  },
  {
    breakpoint: mediaBreakpoints.sm,
    numVisible: 1,
    numScroll: 1
  }
])

export function setLocationToEnquiryPage(enquiryType: EnquiryType, cid: number | null): void {
  const env = process.env.NODE_ENV
  let url = ''
  if (enquiryType === EnquiryType.Direct) {
    env === 'development' ? (url = `/enquirypage#cid=${cid!}`) : (url = `/enquiry#cid=${cid!}`)
  } else if (enquiryType === EnquiryType.General) {
    env === 'development' ? (url = '/enquirypage') : (url = '/enquiry')
  }
  window.location.href = url
}

export function getFilterDto(cids: number[], directBoarding?: number | undefined): IEnquiryFilter {
  //excludedCompanies is used to filter out companies for similaraccos on enquiry success page
  if (filterStore.value != null) {
    return {
      areaIds: filterStore.value.areaId !== 0 && filterStore.value.areaId != null ? [filterStore.value.areaId] : [],
      regionIds: null!,
      themeIds: filterStore.value.themeId !== 0 && filterStore.value.themeId != null ? [filterStore.value.themeId] : [],
      accotypes: filterStore.value.accotypeIds.map((id) => ({
        id,
        ratingFrom: filterStore.value.ratingFrom,
        ratingTo: filterStore.value.ratingTo
      })),
      amenityIds: filterStore.value.amenityIds,
      boardingIds: directBoarding != null ? [directBoarding] : filterStore.value.boardingIds,
      excludeCompanyIds: getExcludedCompanies(cids) // this property is misused in order to get not already requested cids in similar accos for enquiry success page
    }
  } else {
    return {
      areaIds: undefined,
      regionIds: undefined,
      themeIds: undefined,
      accotypes: undefined,
      amenityIds: undefined,
      boardingIds: undefined,
      excludeCompanyIds: getExcludedCompanies(cids) // this property is misused in order to get not already requested cids in similar accos for enquiry success page
    }
  }
}

function getExcludedCompanies(cids: number[]): number[] {
  //this is used, to be able to filter out already requested cids for the similar accos on the enquiry success page
  var localStorageData = localStorage.getItem(RequestedCidsLocalStorage)
  if (localStorageData != null) {
    const storedData: number[] = JSON.parse(localStorageData)
    cids.forEach((cid) => {
      if (!storedData.includes(cid)) storedData.push(cid)
    })
    localStorage.setItem(RequestedCidsLocalStorage, JSON.stringify(storedData))
    return storedData
  } else {
    localStorage.setItem(RequestedCidsLocalStorage, JSON.stringify(cids))
    return cids
  }
}

function castFilterStoreToAccosFilter(filterStore: IFilterStoreInterface): IAccosFilter {
  const accosFilter: IAccosFilter = {
    areaId: filterStore.areaId,
    themeId: filterStore.themeId,
    accotypes: filterStore.accotypeIds.length
      ? filterStore.accotypeIds.map((id) => ({
          id,
          ratingFrom: filterStore.ratingFrom,
          ratingTo: filterStore.ratingTo
        }))
      : undefined,
    amenityIds: filterStore.amenityIds,
    boardingIds: filterStore.boardingIds,
    companyIds: filterStore.companyIds
  }

  return accosFilter
}
